import { Controller } from "@hotwired/stimulus"
import Choices from 'choices.js'

export default class extends Controller {
  static targets = ["select", "stats", "saveButton", "hiddenField"]
  static values = {
    url: String,
    placeholder: { type: String, default: "Search users, prospects, coaches..." },
    existing: { type: Array, default: [] }
  }

  connect() {
    this.selectedItems = []
    this.initializeChoices()

    // Update stats after initialization if there are pre-selected items
    if (this.choices.getValue(true).length > 0) {
      this.updateStats()
      this.updateHiddenField()
    }
  }

  disconnect() {
    if (this.choices) {
      this.choices.destroy()
    }
  }

  initializeChoices() {
    this.choices = new Choices(this.selectTarget, {
      removeItemButton: true,
      searchEnabled: true,
      searchChoices: true,
      searchPlaceholderValue: this.placeholderValue,
      placeholder: true,
      placeholderValue: this.placeholderValue,
      noResultsText: 'No results found',
      noChoicesText: 'Start typing to search',
      itemSelectText: 'Click to select',
      loadingText: 'Loading...',
      shouldSort: false,
      renderChoiceLimit: 50,
      searchResultLimit: 50
    })

    // Listen for search
    this.selectTarget.addEventListener('search', (event) => {
      this.handleSearch(event.detail.value)
    })

    // Listen for changes
    this.selectTarget.addEventListener('change', () => {
      this.updateStats()
    })

    // Listen for item removal
    this.selectTarget.addEventListener('removeItem', () => {
      this.updateStats()
    })
  }

  async handleSearch(query) {
    if (!query || query.length < 2) return

    try {
      const response = await fetch(`${this.urlValue}?q=${encodeURIComponent(query)}`)
      const data = await response.json()

      // Clear existing choices
      this.choices.clearChoices()

      // Add new choices with custom templates
      // Store full recipient info in the value so we can display it later
      const choices = data.map(recipient => ({
        value: JSON.stringify({
          type: recipient.type,
          id: recipient.id,
          name: recipient.name,
          email: recipient.email,
          phone: recipient.phone
        }),
        label: this.formatLabel(recipient),
        customProperties: {
          recipient: recipient
        }
      }))

      this.choices.setChoices(choices, 'value', 'label', true)
    } catch (error) {
      console.error('Search failed:', error)
    }
  }

  formatLabel(recipient) {
    const icon = this.getIconEmoji(recipient.icon)
    const contact = recipient.email || recipient.phone || ''
    return `${icon} ${recipient.name} (${recipient.type}) - ${contact}`
  }

  getIconEmoji(type) {
    const icons = {
      user: '👤',
      prospect: '🌱',
      coach: '🏆',
      institution: '🏛️',
      account: '💼'
    }
    return icons[type] || '👤'
  }

  updateStats() {
    const selectedValues = this.choices.getValue(true)

    if (selectedValues.length === 0) {
      this.statsTarget.innerHTML = '<span class="text-sm text-gray-500">No recipients selected</span>'
      if (this.hasSaveButtonTarget) {
        this.saveButtonTarget.classList.add('hidden')
      }
      this.updateHiddenField()
      return
    }

    // Parse selected items
    const recipients = selectedValues.map(val => {
      try {
        return JSON.parse(val)
      } catch {
        return null
      }
    }).filter(Boolean)

    // Count unique by type
    const typeCounts = recipients.reduce((acc, r) => {
      acc[r.type] = (acc[r.type] || 0) + 1
      return acc
    }, {})

    const countText = Object.entries(typeCounts)
      .map(([type, count]) => `${count} ${type.toLowerCase()}${count > 1 ? 's' : ''}`)
      .join(', ')

    this.statsTarget.innerHTML = `
      <div class="flex items-center gap-4">
        <span class="text-sm font-medium text-gray-900">${selectedValues.length} selected</span>
        <span class="text-sm text-gray-500">${countText}</span>
      </div>
    `

    if (this.hasSaveButtonTarget) {
      this.saveButtonTarget.classList.remove('hidden')
    }

    this.updateHiddenField()
  }

  updateHiddenField() {
    if (!this.hasHiddenFieldTarget) return

    const selectedValues = this.choices.getValue(true)
    const recipients = selectedValues.map(val => {
      try {
        return JSON.parse(val)
      } catch {
        return null
      }
    }).filter(Boolean)

    // Store in the format expected by the backend: {specific_recipients: [...]}
    const filterCriteria = {
      specific_recipients: recipients
    }

    this.hiddenFieldTarget.value = JSON.stringify(filterCriteria)
  }

  saveAsAudienceList(event) {
    event.preventDefault()

    const listName = prompt('Enter a name for this audience list:')
    if (!listName) return

    const selectedValues = this.choices.getValue(true)
    const recipients = selectedValues.map(val => JSON.parse(val))

    // Create a form and submit it
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = '/admin/audience_lists'

    // CSRF token
    const csrfToken = document.querySelector('meta[name="csrf-token"]').content
    const csrfInput = document.createElement('input')
    csrfInput.type = 'hidden'
    csrfInput.name = 'authenticity_token'
    csrfInput.value = csrfToken
    form.appendChild(csrfInput)

    // Name
    const nameInput = document.createElement('input')
    nameInput.type = 'hidden'
    nameInput.name = 'audience_list[name]'
    nameInput.value = listName
    form.appendChild(nameInput)

    // Target type (specific for mixed recipients)
    const targetTypeInput = document.createElement('input')
    targetTypeInput.type = 'hidden'
    targetTypeInput.name = 'audience_list[target_type]'
    targetTypeInput.value = 'specific'
    form.appendChild(targetTypeInput)

    // Store recipients in filter_criteria
    const filterInput = document.createElement('input')
    filterInput.type = 'hidden'
    filterInput.name = 'audience_list[filter_criteria]'
    filterInput.value = JSON.stringify({
      specific_recipients: recipients
    })
    form.appendChild(filterInput)

    document.body.appendChild(form)
    form.submit()
  }
}
